import React, { useEffect, useRef, useState } from 'react';
import { useRoute } from 'react-router5';
import { connect } from 'react-redux';

import {
  aggregateByRoots, aggregateBySubroots, loadThemedTiledLayer, aggregateRangeSettings,
  upgradeSettingLayers, handleClick, buildUpdator
} from './shared';

import { buildLegend } from './legend';

// group by subroot or root given zoom level
// aggregate_range is of the form:
// {level0: [0, 1], level1: [2, 3], level2: [4, 5] }
function aggregateByPOIGeo(nodes, new_zl, agg_range) {
  const lvl = ["level0", "level1", "level2"].find(x => agg_range[x].includes(new_zl));

  switch (lvl) {
    case 'level0': return aggregateByRoots(nodes);
    case 'level1': return aggregateBySubroots(nodes);
    case 'level2': return nodes.slice();
    default: return false;
  }
}

function cleanUpAggregate(nodes) {
  return nodes
    // remove aggs that dont have any posts associated to it (could be done at start?)
    .map(x => x.aggs ? {...x, aggs: x.aggs.filter(y => y.contacts.length || y.entities.length)} : x)
    // only take points that either have posts associated to it
    // or who have aggregate that have
    .map(n => {
      const all_entities = (n.aggs || []).flatMap(x => x.entities).concat(n.entities);
      const all_contacts = (n.aggs || []).flatMap(x => x.contacts).concat(n.contacts);

      return {...n, all_entities, all_contacts}
    })
    .filter(x => x.all_entities.length || x.all_contacts.length)
    .map(x => {
      x.style = {
	label: x.label + "<br>" +
	       (x.all_entities.length ? ` ${x.all_entities.length} E` : '') +
	       (x.all_contacts.length ? ` ${x.all_contacts.length} C` : '')
      }
      return x;
    })
}


const makeOnZoomLevelChanged = (agg_range) => {
  return function onZoomLevelChanged(nodes, new_zl, prev_zl) {
    // aggs: geo_aggregations
    const new_nodes = aggregateByPOIGeo(nodes, new_zl, agg_range);

    if (new_nodes === false) {
      var aggs = [], ce = [];

      // deaggregate returns 3 differents kind of data:
      // 1. aggregates (w/ contacts/entities keys)
      // and standalones 2. contact 3. entity
      deaggregate(nodes).forEach((x) => {
	if (x.contacts) { aggs.push(x) } else { ce.push(x) }
      })

      return ce.concat(cleanUpAggregate(aggs));
    }

    return cleanUpAggregate(new_nodes);
  }
}

function initializeChart(data, opts) {
  const {
    element, height, navigation, onClick, onClickAgg, initial_theme, onZoomLevelChanged,
    router, layers: layers_from_settings, data: data_from_settings, legend
  } = opts;

  let current_zoom = navigation.initialZoom;

  const new_nodes = onZoomLevelChanged(data.nodes, current_zoom, current_zoom)
  // will be updated by updateChart
  let mutable_data = data;

  const theme_tiled_loader = loadThemedTiledLayer(initial_theme);

  const updator = buildUpdator(element);

  let chart = new window.GeoChart({
    container: element,
    area: {
      height: height,
    },
    background: { enabled: false }, // deactivate default tile map
    navigation: navigation,
    data: data_from_settings.concat([{ preloaded: {...data, nodes: new_nodes}, id: "defaultData" }]),
    events: {
      onHoverChange: updator.onHoverChange,
      onPointerDrag: (e, args) => { updator.update(e, args); },
      onSettingsChange: (e, args) => { updator.update(e, args); },
      onChartUpdate: (e, args) => {
	updator.update(e, args);
	theme_tiled_loader(e.chart);
      },
      onPositionChange: function(e, args) {
	updator.update(e, args);

        const prev_zl = current_zoom;
        const new_zl = chart.zoomLevel();

        if(new_zl !== prev_zl) {
          current_zoom = new_zl;

          // aggregate on root/subroot
          const new_nodes = onZoomLevelChanged(mutable_data.nodes, new_zl, prev_zl);
          chart.replaceData(
            {...mutable_data, nodes: new_nodes },
            "defaultData"
          )
        }
      },
      onClick: function(e) {
	const click_element = e.clickNode || e.clickLink;

        if (!click_element)
	  return;

	const settings = click_element.layer.layerSettings;

	if (settings.id == "default") {
          // look for next step where we can see more details
          // let new_zoom = current_zoom <= 4 ? 5 : (current_zoom <= 7 ? 8 : current_zoom + 1);
          // chart.zoomLevel(new_zoom);
	  
	  const data = click_element.data;

          const {all_entities, all_contacts, id} = data;

          if (all_entities || all_contacts) {
            // nothing
            onClickAgg({entities: all_entities.map(x => x.value),
                        contacts: all_contacts.map(x => x.value), poi_geo_id: data.id})
          } else if (id.startsWith("entity")) {
            const entity = data.value;
            onClick(entity.id, "entity", entity)
          } else if (id.startsWith("contact")) {
            const contact = data.value;
            onClick(contact.id, "contact", contact);
          }

          e.preventDefault();
        } else {
	  handleClick(click_element, router);
	}
      }
    },
    layers: upgradeSettingLayers(layers_from_settings).concat([
        {
            id: "default",
            type: "items",
            data: {id: "defaultData"},
            aggregation: {
              enabled: false
            },
        }
      // https://zoomcharts.com/developers/en/geo-chart/examples/charts-on-charts/piechart-on-geochart.html
      // https://zoomcharts.com/developers/en/geo-chart/charts-on-charts/layers.html
      // https://demo.zoomcharts.com/pie-chart/examples/gallery/pie-chart-gauge-drilldown-live-data.html
      /*
        , {
          id: "pie",
          type: "charts",
          shapesLayer: "items",
          chartType: "piechart",
          settingsFunction: function (node, data) {

            if (data.contacts.length + data.entities.length <= 1) {
              return;
            }

            console.log("Node: ", node);
            console.log("Data: ", data);

            let c_pie = { value: data.contacts.length, name: "Contacts", subvalues: []};
            let e_pie = { value: data.entities.length, name: "Entities", subvalues: []};

            var sliceColors = ["lightskyblue", "lightcoral"];

            const pieData = { subvalues: [c_pie, e_pie] };

            function insideStyle(slice, data) {
              slice.label.text = null;
              slice.insideLabel.text = data.value;
            }

            return {
              pie: {
                radius: 20,
                startAngle: -Math.PI,
                endAngle: 0,
                innerRadius: 0,
                style: {
                  sliceColors: sliceColors,
                  colorDistribution: "list"
                }
              },
              data: { preloaded: pieData },
              events: {
                onClick: (e, args) => {
                  console.log("CLICKED");
                }
              },
              // labels: { insideLabel: "always" },
              labels: { insideLabel: "auto" },
              slice: {
                styleFunction: insideStyle
              }
            }
          }
        }
        */
    ]),
  })

  updator.setChart(chart);
  buildLegend(chart, element, legend);

  function updateChart(new_data) {
    mutable_data = new_data;

    const current_zoom = chart.zoomLevel();
    const new_nodes = onZoomLevelChanged(mutable_data.nodes, current_zoom, current_zoom)

    const cb = () => {
      chart.replaceData(
	{...mutable_data, nodes: new_nodes },
	"defaultData"
      )
    }
    const timeout = () => {
      if (!chart.leaflet()) {
	setTimeout(timeout, 50);
      } else {
	cb();
      }
    }

    timeout();
  }

  function onThemeUpdate(theme) {
    theme_tiled_loader(chart, theme)
  }

  return { chart, updateChart, onThemeUpdate  }
}
function buildContactData(contacts) {
  return contacts
    // .filter(x => x.lon && x.lat)
    .map(x => {
      return {id: `contact_${x.id}`, type: 'contact', loaded: true, value: x};
    });
}

function buildEntityData(entities) {
  return entities
    // .filter(x => x.lon && x.lat)
    .map(x => {
      return {id: `entity_${x.id}`, type: 'entity', loaded: true, value: x};
    })
}
// raw points
function buildData({ geos, contacts, entities }) {

  // transform geo tree into list
  const geo_list = geos.flatMap(root => {
    return [root].concat(root.children.flatMap(subroot => {
      return [subroot].concat(subroot.children)
    }))
  }).filter(x => x.lat && x.lon)

  // list of all geo_points (hierarchy does not matter
  // w/ contacts/entities associated directly to it
  const points = geo_list.map(x => {
    return {...x,
      type: "point",
      coordinates: [parseFloat(x.lon), parseFloat(x.lat)],
      label: x.item,
      contacts: [],
      entities: []
    }
  })

  // assigning post to their geo point
  let geo_dict = {};
  points.forEach(g => { geo_dict[g.id] = g; })


  const new_contacts = buildContactData(contacts)
  const new_entities = buildEntityData(entities)

  // contacts and entities may not contain a geographic point (still optional)
  new_contacts.forEach(x => {
    if (x.value.poi_geo_id) { geo_dict[x.value.poi_geo_id].contacts.push(x); }
  })
  new_entities.forEach(x => {
    if (x.value.poi_geo_id) { geo_dict[x.value.poi_geo_id].entities.push(x); }
  })

  // removing leaves w/ 0 contacts/entities associated
  const filtered_points = points.filter(x => {
    return !(x.root && x.subroot && (x.entities.length + x.contacts.length === 0))
  })

  return {nodes: filtered_points}
}

function focusOnNode(focusNode, focused, setFocused, data, chart, zoom_level){
    let findNode = [];
    if(focusNode.type === "contact"){
      findNode = data.nodes.find(n => {
        return n.contacts.find(c=>c.value.id===focusNode.id);
      });
    }else{
      findNode = data.nodes.find(n => {
        return n.entities.find(c=>c.value.id===focusNode.id);
      });
    }
    if(findNode && !focused){
      chart.leaflet().flyTo({lat : findNode.lat, lon : findNode.lon}, zoom_level);
      setFocused(true);
    }
}
// nodes are geopoint w/ their association to contact/entities
// build "fake" geopoint w/ those contacts/entities
function deaggregate(nodes) {
  const hasLonLat = (x) => x.value.lon && x.value.lat

  return nodes.flatMap(node => {
    const contact_fake_agg = node.contacts.filter(hasLonLat).map(n => {
      const c = n.value;
      return {
	id: n.id,
	type: "point",
	coordinates: [c.lon, c.lat],
	style: {
	  label: `${c.firstname} ${c.lastname}`,
	  image: c.img,
	  imageCropping: true,
	  display: 'image'
	},
	value: c
      }
    });

    const entity_fake_agg = node.entities.filter(hasLonLat).map(n => {
      const e = n.value;
      return {
	id: n.id,
	type: "point",
	coordinates: [e.lon, e.lat],
	style: {
	  label: e.name,
	  image: e.img,
	  imageCropping: true,
	  display: 'rectangle'
	},
	value: e
      }
    });

    // all aggregated values had lonlat, the aggregate itself is empty and not returned
    if (entity_fake_agg.length === node.entities.length &&
	contact_fake_agg.length === node.contacts.length) {
      return contact_fake_agg.concat(entity_fake_agg);
    }

    // keep the node w/ contacts/entites who have no lonlat associated
    // they will just show up as geo point
    const new_node = {
      ...node,
      contacts: node.contacts.filter(x => !hasLonLat(x)),
      entities: node.entities.filter(x => !hasLonLat(x)),
    };

    return [new_node].concat(contact_fake_agg, entity_fake_agg);
  })
}

const getNoAggLevel = ({ level0, level1, level2 }) => {
  return level0.concat(level1, level2).reduce((acc, lvl) => Math.max(acc, lvl), -1);
}

// build settings from backoffice params
function buildSettings(settings) {
  // default agg_range
  const agg_range = aggregateRangeSettings(
    settings.aggregate_range,
    {level0: [0, 1], level1: [2, 3], level2: [4, 5]}
  )

  const no_agg_level = Math.min(getNoAggLevel(agg_range) + 1, 13);

  return {
    navigation: {initialZoom: 3, ...(settings.navigation || {})},
    onZoomLevelChanged: makeOnZoomLevelChanged(agg_range),
    aggregate_range: agg_range,
    no_aggregate_level: no_agg_level,
    layers: settings.layers || [],
    data: settings.data || [],
    legend: settings.legend || []
  }
}


function CartoGeoCE({ contacts, entities, geos, onClick, onClickAgg, currentTheme, map_settings, focusNode, focused, setFocused }) {
  const ref = useRef();
  const { router } = useRoute();

  const [chartAPI, setChartAPI] = useState();

  const settings = buildSettings(map_settings || {});

  useEffect(() => {
    const d = buildData({ contacts, entities, geos });

    if (!chartAPI) {
      const opts = {
	...settings,
        element: ref.current,
        height: ref.current.parentNode.offsetHeight,
        initial_theme: currentTheme,
	router,
        onClick: onClick || (() => {}),
        onClickAgg: onClickAgg || (() => {})
      };

      const chartAPI = initializeChart(d, opts);
      setChartAPI(chartAPI);

      if(focusNode != null){
	focusOnNode(focusNode, focused, setFocused, d, chartAPI.chart, settings.no_aggregate_level);
      }
    } else {
      chartAPI.updateChart(d);
      if(focusNode != null){
	focusOnNode(focusNode, focused, setFocused, d, chartAPI.chart, settings.no_aggregate_level);
      }
    }

  // fixme? contacts/entities/geos array object change but not its content?
  // rely on length to avoid unecessary updates
  // when navigating contacts/entites etc. may not be set (useRouteNode problem?) ensure they are here
  }, [contacts && contacts.length, entities && entities.length, geos && geos.length, focusNode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chartAPI) {
      chartAPI.onThemeUpdate(currentTheme);
    }
  }, [chartAPI, currentTheme])

  return <div ref={ref} />

}

const mapStateToProps = (state) => ({ currentTheme: state.theme.value });

export default connect(mapStateToProps, null)(CartoGeoCE);
